import { Component, OnInit, Renderer2, ViewChildren } from '@angular/core';
import { NavigationStart, Router, RouterEvent } from '@angular/router';

import { AppService } from '../../services/app.service';

import { ParameterService } from '../../services/parameter.service';
import { NgZone } from '@angular/core';
import { ElementRef } from '@angular/core';
import { AppDatePipe } from '../../pipes/date.pipe';
import { ImpersonationService } from '../../services/impersonation.service';
import { Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { QueryList } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { User } from '../../models/Auth';

class NavItem {
    title?: string;
    route?: string;
    icon?: string;
    children?: NavGroup[] = [];
    isOpened?: boolean;
    visibility?: () => boolean;

    get isHidden() {
        return !this.isVisible;
    }

    get isVisible() {
        return this.visibility === undefined
            || (this.visibility() && (!this.children?.length || this.children.some(c => c.isVisible)));
    }
}

class NavGroup {
    constructor(public items: NavItem[]) { }

    get isVisible() {
        return this.items.some(c => c.isVisible);
    }
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
    constructor(
        private app: AppService,
        private auth: AuthService,
        private parameters: ParameterService,
        private impersonation: ImpersonationService,
        private router: Router,
        private zone: NgZone,
        private renderer: Renderer2,
        private appDate: AppDatePipe,
        @Inject(DOCUMENT) private document: Document
    ) { }

    date = new Date();

    navItems: NavItem[] = [];

    readonly showDebugBadge = this.app.env.showDebugMsg;

    userName: any = {};
    menuOpened: boolean;
    locale: string;
    rsuSiteUrl: string;
    myRsuSiteUrl: string;
    eStudiesSiteUrl: string;
    searchOpened: boolean;
    currentUser: User = this.app.currentUser;

    readonly showLanguageSwitch = this.app.env.showLanguageSwitch;

    get isStudent(): boolean {
        return this.app.currentUser?.isStudent;
    }

    @ViewChildren('currentDate') private currentDateEl: QueryList<ElementRef>;
    @ViewChildren('currentTime') private currentTimeEl: QueryList<ElementRef>;

    ngOnInit() {
        this.locale = this.app.currentLanguage || 'lv';

        this.auth.user.subscribe(user => {
            this.currentUser = user;
            this.buildNavItems();
        });

        this.router.events.subscribe((e) => {
            this.interceptNavigation(e as RouterEvent);
        });

        this.app.onDocumentClick().subscribe((e) => {
            const opened = this.navItems.filter((t) => t.isOpened);

            if (!e.target.closest('.site-nav-toggle') && !e.target.closest('.site-nav-sub')) {
                if (opened.length) {
                    opened.forEach(t => t.isOpened = false);
                }

                if (this.menuOpened) {
                    this.menuOpened = false;
                }
            }
        });

        this.app.addLoading(this.parameters.getValues()).subscribe(data => {
            this.rsuSiteUrl = data.find(t => t.Code == 'RsuSiteUrl').Value;
            this.myRsuSiteUrl = data.find(t => t.Code == 'MyRsuSiteUrl').Value;
            this.eStudiesSiteUrl = data.find(t => t.Code == 'EStudiesSiteUrl').Value;
        });

        this.zone.runOutsideAngular(() => {
            setInterval(() => {
                const d = new Date();

                const date = this.appDate.transform(d);
                const time = `0${d.getHours()}`.slice(-2) + ':' + `0${d.getMinutes()}`.slice(-2);

                this.currentDateEl?.forEach(t => {
                    this.renderer.setProperty(t.nativeElement, 'textContent', date);
                });

                this.currentTimeEl?.forEach(t => {
                    this.renderer.setProperty(t.nativeElement, 'textContent', time);
                });
            }, 100);
        });
    }

    openMenuItem(menuItem: any) {
        this.closeAll();

        if (menuItem.children && menuItem.children.length) {
            menuItem.isOpened = true;
        }
    }

    closeMenuItem(menuItem: any) {
        menuItem.isOpened = false;
    }

    logout() {
        this.app.showLoading();
        this.auth.logout();
    }

    changeLocale(locale: string) {
        const route = this.app.currentUrl.split('/');

        if (!route.length) {
            route.push(locale);
        } else {
            route[1] = locale;
        }

        // must reload because of pipes and LOCALE_ID
        location.href = route.join('/');
    }

    closeAll() {
        this.navItems.forEach(t => {
            t.isOpened = false;
        });

        this.menuOpened = false;
    }

    endImpersonation() {
        this.app.addLoading(this.impersonation.end()).subscribe(user => {
            this.currentUser = user;
        });
    }

    toggleMenu() {
        this.menuOpened = !this.menuOpened;
    }

    toggleSearch() {
        this.searchOpened = !this.searchOpened;

        const body = this.document.querySelector('#app-body');

        if (this.searchOpened) {
            body.classList.add('d-none');
        } else {
            body.classList.remove('d-none');
        }
    }

    private interceptNavigation(event: RouterEvent) {
        if (event instanceof NavigationStart) {
            this.closeAll();
        }
    }

    private buildNavItems() {
        this.navItems.length = 0;

        const t = (key: string) => this.app.translate(key);
        const u = this.currentUser;

        const build = (opts: { route?: string, title: string, icon?: string, visibility?: () => boolean }) => {
            const item = new NavItem();
            item.route = opts.route;
            item.title = opts.title;
            item.icon = opts.icon;
            item.visibility = opts.visibility;
            return item;
        };

        this.navItems.push(build({
            route: '/impersonation',
            title: t('nav_impersonation'),
            visibility: () => u && !u.impersonator && u.hasRight('ES_MAIN.IMPERSONATION.SET') && u.hasRight('ES_MAIN.STUDENTS.FIND'),
        }));

        const admin = new NavItem();
        admin.title = t('nav_admin');
        admin.children = [
            new NavGroup([
                build({ title: t('nav_config'), route: '/parameters', icon: 'settings-sliders', visibility: () => u?.isAdmin }),
                build({ title: t('nav_classifiers'), route: '/classifiers', icon: 'layers', visibility: () => u?.isAdmin }),
                build({
                    title: t('nav_messages'),
                    route: '/messages',
                    icon: 'text',
                    visibility: () => u?.rights.some(t => t.startsWith('UDR.CERT.'))
                }),
                build({
                    title: t('nav_templates'),
                    route: '/templates',
                    icon: 'document',
                    visibility: () => u?.rights.some(t => t.startsWith('ES_MAIN.TEMPLATES.') || t.startsWith('UDR.CERT.'))
                }),
                build({ title: t('nav_tiles'), route: '/tiles', icon: 'picture', visibility: () => u?.hasRight('ES_MAIN.TILES.VIEW') }),
                build({ title: t('nav_userList'), route: '/users', icon: 'users', visibility: () => u?.isAdmin }),
                build({ title: t('nav_permissions'), route: '/permissions', icon: 'key', visibility: () => u?.isAdmin }),
                build({ title: t('nav_log'), route: '/log', icon: 'calendar-lines', visibility: () => u?.isAdmin }),
            ]),
            new NavGroup([
                build({ title: t('nav_applicationConfig'), route: '/applications/config', visibility: () => u?.hasRight('APPLICATION.CONFIG') }),
                build({ title: t('nav_employeeApplicationConfig'), route: '/employee-applications/config', visibility: () => u?.hasRight('EMPLOYEE_APPLICATION.CONFIG') }),
                build({ title: t('nav_externalConfig'), route: '/external/config', visibility: () => u?.hasRight('EXTERNAL_SERVICE.CONFIG') }),
                build({ title: t('nav_ebusSessionConfig'), route: '/ebus/session-config', visibility: () => u?.hasRight('EBUS.SESSION_CONFIG') }),
                build({ title: t('nav_certRequests'), route: '/certificate-requests', visibility: () => u?.hasRight('CERTIFICATE_REQUESTS.VIEW') }),
                build({ title: t('nav_notificationTemplates'), route: '/notification-templates', visibility: () => u?.hasRight('NOTIFICATION_TEMPLATE.VIEW') }),
                build({ title: t('nav_bcContests'), route: '/bc-contests', visibility: () => u?.isAdmin })
            ])
        ];

        this.navItems.push(admin);
    }
}
